import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Minus, MessageCircle } from "lucide-react";
import { cn } from "../../lib/utils";

/* Aceternity frequently-asked-questions-accordion, adapted for BookingFlows. */

const FAQS = [
  {
    q: "How much does BookingFlows cost?",
    qAr: "كم تبلغ تكلفة BookingFlows؟",
    a: "There are no monthly fees and no setup costs. A small 3% platform fee is added on top of each booking and paid by your customer — your service price, add-ons and tax stay yours in full.",
    aAr: "لا توجد رسوم شهرية ولا تكاليف إعداد. تُضاف رسوم منصة بسيطة بنسبة 3% فوق كل حجز ويدفعها عميلك — بينما يبقى سعر خدمتك والإضافات والضريبة لك بالكامل.",
  },
  {
    q: "How fast can we go live?",
    qAr: "ما السرعة التي يمكننا الانطلاق بها؟",
    a: "Most operators are live in about 15 minutes. Create your services, connect payouts, and share your booking link — or embed it straight on your website.",
    aAr: "معظم المشغّلين يكونون جاهزين خلال 15 دقيقة تقريباً. أنشئ خدماتك، واربط حساب التحويلات، وشارك رابط الحجز — أو ضمّنه مباشرة في موقعك.",
  },
  {
    q: "Can you migrate bookings from my current system?",
    qAr: "هل يمكنكم نقل الحجوزات من نظامي الحالي؟",
    a: "Yes — and it's free. Our engineers move your bookings, customers, schedules and gift card balances for you, with zero downtime. You keep taking bookings the whole time.",
    aAr: "نعم — ومجاناً. مهندسونا ينقلون حجوزاتك وعملاءك وجداولك وأرصدة بطاقات الهدايا نيابة عنك، دون أي توقف. وتواصل استقبال الحجوزات طوال الوقت.",
  },
  {
    q: "Do you support UAE operators and AED?",
    qAr: "هل تدعمون المشغّلين في الإمارات والدرهم الإماراتي؟",
    a: "Natively. UAE operators charge in AED through Ziina — licensed by the Central Bank of the UAE — with Arabic booking flows and Gulf Standard Time scheduling. Stripe powers everywhere else.",
    aAr: "بشكل أصيل. يستقبل المشغّلون في الإمارات مدفوعاتهم بالدرهم عبر Ziina المرخّصة من مصرف الإمارات المركزي — مع مسارات حجز بالعربية وجدولة بتوقيت الخليج. وStripe يغطي بقية العالم.",
  },
  {
    q: "Do I need my own website?",
    qAr: "هل أحتاج إلى موقع إلكتروني خاص بي؟",
    a: "No. Every account includes a hosted booking page and a free marketplace listing. Have a site already? Embed the widget with one snippet. Want one? We build competitive, SEO-primed websites too.",
    aAr: "لا. كل حساب يتضمن صفحة حجز مستضافة وإدراجاً مجانياً في السوق. لديك موقع بالفعل؟ ضمّن الأداة بسطر واحد. تريد موقعاً؟ نبني لك مواقع منافسة ومحسّنة لمحركات البحث أيضاً.",
  },
  {
    q: "Who owns my customer data?",
    qAr: "من يملك بيانات عملائي؟",
    a: "You do — full stop. Every company runs in its own silo, encrypted at rest and in transit. Export or delete your data anytime. It is never sold, shared, or used to compete with you.",
    aAr: "أنت — وبلا نقاش. كل شركة تعمل في نطاق معزول خاص بها، مشفّرة أثناء التخزين والنقل. صدّر بياناتك أو احذفها متى شئت. لا تُباع أبداً ولا تُشارك ولا تُستخدم لمنافستك.",
  },
];

const FAQItem = ({ index, question, answer, open, setOpen }) => {
  const isOpen = open === index;
  return (
    <div
      className="cursor-pointer py-5"
      onClick={() => setOpen(isOpen ? null : index)}
      data-testid={`faq-item-${index}`}
    >
      <div className="flex items-start">
        <div className="relative mr-4 mt-0.5 h-6 w-6 flex-shrink-0 rtl:ml-4 rtl:mr-0">
          <Plus
            className={cn(
              "absolute inset-0 h-6 w-6 transform text-blue-600 transition-all duration-200",
              isOpen && "rotate-90 scale-0"
            )}
          />
          <Minus
            className={cn(
              "absolute inset-0 h-6 w-6 rotate-90 scale-0 transform text-blue-600 transition-all duration-200",
              isOpen && "rotate-0 scale-100"
            )}
          />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg">{question}</h3>
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="overflow-hidden"
              >
                <p className="pt-2 text-sm leading-relaxed text-gray-500 sm:text-base">{answer}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export const FaqSection = ({ isAr, onContact }) => {
  const [open, setOpen] = useState(null);
  return (
    <section className="bg-white py-24" data-testid="supplier-faq-section" dir={isAr ? "rtl" : undefined}>
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 sm:px-6 md:grid-cols-2 md:gap-16 lg:px-8">
        <div>
          <div className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
            <MessageCircle className="h-3.5 w-3.5" />
            {isAr ? "الأسئلة الشائعة" : "FAQ"}
          </div>
          <h2 className="text-4xl font-bold leading-[1.1] tracking-tight text-gray-900 md:text-5xl">
            {isAr ? (
              "أسئلة يطرحها المشغّلون"
            ) : (
              <>
                Questions operators{" "}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  actually ask.
                </span>
              </>
            )}
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-gray-500 sm:text-lg">
            {isAr
              ? "كل ما يسأل عنه المشغّلون قبل الانتقال إلينا. لديك سؤال آخر؟ تحدث معنا مباشرة."
              : "Everything operators want to know before switching. Something else on your mind? Talk to a real human."}
          </p>
          <button
            onClick={onContact}
            className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
            data-testid="faq-contact-cta"
          >
            {isAr ? "تواصل مع الدعم" : "Ask us anything"}
            <span className="transition-transform group-hover:translate-x-1 rtl:rotate-180">→</span>
          </button>
        </div>
        <div className="divide-y divide-gray-100">
          {FAQS.map((faq, index) => (
            <FAQItem
              key={index}
              index={index}
              question={isAr ? faq.qAr : faq.q}
              answer={isAr ? faq.aAr : faq.a}
              open={open}
              setOpen={setOpen}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
